"use client";

import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { applyReleaseSchedule } from "@/lib/actions/release-schedule-actions";
import { addCalendarDays, buildReleaseDates, localDate, validateReleaseDates, type ReleasePlan } from "@/lib/releases/schedule";
import type { PublishingRow } from "@/lib/paywall/queries";
import { ReleasePlanFields } from "./release-plan-fields";
export function ReleaseSchedulePanel({
  storyId,
  rows,
  onClose,
  onSaved,
  onBusy
}: {
  storyId: string;
  rows: PublishingRow[];
  onClose: () => void;
  onSaved?: () => void;
  onBusy?: (busy: boolean) => void;
}) {
  const router = useRouter();
  const [plan, setPlan] = useState<ReleasePlan>(() => {
    const timeZone = rows.length === 1 && rows[0].releaseTimeZone ? rows[0].releaseTimeZone : Intl.DateTimeFormat().resolvedOptions().timeZone;
    const scheduled = rows.length === 1 ? rows[0].scheduledReleaseAt : null;
    return {
      date: scheduled ? localDate(new Date(scheduled), timeZone) : addCalendarDays(localDate(new Date(), timeZone), 1),
      slot: scheduled ? new Date(scheduled).toISOString() : "",
      timeZone
    };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preview = useMemo(() => {
    if (!plan.date || !plan.slot) return {
      dates: [],
      error: null
    };
    try {
      const dates = buildReleaseDates(plan, rows.length);
      validateReleaseDates(dates, plan.timeZone);
      return {
        dates,
        error: null
      };
    } catch (error_0) {
      return {
        dates: [],
        error: error_0 instanceof Error ? error_0.message : "Invalid schedule"
      };
    }
  }, [plan, rows.length]);
  const warnings = rows.filter((row, index) => row.accessTier === "premium" && row.freeAfter && preview.dates[index] && new Date(row.freeAfter) <= new Date(preview.dates[index])).length;
  const cancellable = rows.filter(row_0 => row_0.scheduledReleaseAt !== null);
  async function save(cancel: boolean) {
    setSaving(true);
    onBusy?.(true);
    setError(null);
    try {
      const targets = cancel ? cancellable : rows;
      const result = await applyReleaseSchedule(storyId, targets.map((row_1, index_0) => ({
        id: row_1.id,
        version: row_1.releaseVersion,
        releaseAt: cancel ? null : preview.dates[index_0]
      })), plan.timeZone);
      if ("error" in result) {
        setError(result.error);
        router.refresh();
        return;
      }
      toast.success(`${result.count} chapter schedule${result.count === 1 ? "" : "s"} ${cancel ? "canceled" : "saved"}`);
      router.refresh();
      onSaved?.();
      onClose();
    } catch (error_1) {
      setError(error_1 instanceof Error ? error_1.message : "Unable to save the schedule");
    } finally {
      setSaving(false);
      onBusy?.(false);
    }
  }
  return <section aria-label="Chapter release settings" className="space-y-4">
            <header className="space-y-1">
                <h3 className="text-sm font-medium">{rows.length === 1 ? "Schedule release" : `Schedule ${rows.length} chapters`}</h3>
            </header>
            <ReleasePlanFields plan={plan} disabled={saving} onChange={next => {
      setPlan(next);
      setError(null);
    }} />
            {(error || preview.error) && <p role="alert" className="text-sm text-destructive">{error || preview.error}</p>}
            {warnings > 0 && <p role="status" className="rounded-md bg-amber-500/10 p-3 text-sm">{warnings} chapter(s) will be free at release. Check Access settings.</p>}
            <div role="group" aria-label="Schedule actions" className="flex flex-wrap justify-end gap-2">
                {cancellable.length > 0 ? <Button variant="destructive" disabled={saving} onClick={() => save(true)}>Cancel {cancellable.length === 1 ? "schedule" : `${cancellable.length} schedules`}</Button> : <Button variant="outline" disabled={saving} onClick={onClose}>Cancel</Button>}
                <Button disabled={saving || !preview.dates.length} onClick={() => save(false)}>{saving ? "Saving…" : "Schedule"}</Button>
            </div>
    </section>;
}